import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, requestErrorMessage } from "@/lib/erp";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUpRight, BriefcaseBusiness, ExternalLink, Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";

const appDestinations = [
  { title: "Customers & vendors", description: "Manage company contacts and customer accounts.", path: "/parties/customers" },
  { title: "Products & inventory", description: "Maintain your catalog and monitor stock.", path: "/products" },
  { title: "Sales & invoices", description: "Create and review invoices, quotations, and orders.", path: "/sales/invoices" },
  { title: "Purchases", description: "Track vendor bills, purchase orders, and returns.", path: "/purchases/invoices" },
  { title: "Payments", description: "Review recorded customer payments.", path: "/payments" },
  { title: "CRM", description: "Follow up with customers and manage sales activity.", path: "/crm" },
  { title: "Reports", description: "Review business and financial reporting.", path: "/reports" },
  { title: "GST reports", description: "Review tax reports and GST-related activity.", path: "/gst/reports" },
  { title: "Expenses", description: "Record and review company expenses.", path: "/expenses" },
  { title: "Approvals", description: "Review items awaiting approval.", path: "/approvals" },
  { title: "Notifications", description: "See company updates and notifications.", path: "/notifications" },
];

export function AdvancedSettings() {
  const navigate = useNavigate();
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="advanced-settings">
      {appDestinations.map((item) => (
        <Card key={item.path} className="p-5 flex flex-col items-start gap-3">
          <div className="flex items-center gap-2 font-semibold"><BriefcaseBusiness className="h-4 w-4 text-brand-600" />{item.title}</div>
          <p className="text-sm text-muted-foreground flex-1">{item.description}</p>
          <Button variant="outline" size="sm" onClick={() => navigate(item.path)}>
            Open {item.title}<ArrowUpRight className="h-4 w-4 ml-2" />
          </Button>
        </Card>
      ))}
    </div>
  );
}

const socialFields = [
  { key: "social_website", label: "Company website", placeholder: "https://example.com" },
  { key: "social_instagram", label: "Instagram", placeholder: "https://instagram.com/your-company" },
  { key: "social_facebook", label: "Facebook", placeholder: "https://facebook.com/your-company" },
  { key: "social_linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/company/your-company" },
];

function safeWebUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  try {
    const url = new URL(/^[a-z][a-z\d+.-]*:/i.test(raw) ? raw : `https://${raw}`);
    return ["http:", "https:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

export function SocialLinksSettings({ form = {}, set }) {
  const update = (key, value) => typeof set === "function" && set(key, value);
  const phone = String(form.social_whatsapp || "").trim();
  const whatsappDigits = phone.replace(/\D/g, "");
  const whatsappHref = whatsappDigits.length >= 7 ? `https://wa.me/${whatsappDigits}` : null;
  return (
    <Card className="p-6 space-y-5" data-testid="social-links-settings">
      <div>
        <h3 className="font-semibold">Company social links</h3>
        <p className="text-sm text-muted-foreground mt-1">These details are saved with Settings when you use the page’s Save button.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {socialFields.map(({ key, label, placeholder }) => {
          const preview = safeWebUrl(form[key]);
          return (
            <div key={key}>
              <Label htmlFor={key}>{label}</Label>
              <Input id={key} type="text" autoComplete="url" value={form[key] || ""} placeholder={placeholder}
                onChange={(event) => update(key, event.target.value)} className="mt-1.5" data-testid={key} />
              {preview ? <a href={preview} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-brand-700 hover:underline">
                Preview link <ExternalLink className="h-3 w-3" />
              </a> : form[key] ? <p className="mt-2 text-xs text-muted-foreground">Enter a valid web address to preview it.</p> : null}
            </div>
          );
        })}
        <div>
          <Label htmlFor="social_whatsapp">WhatsApp phone</Label>
          <Input id="social_whatsapp" type="tel" autoComplete="tel" value={form.social_whatsapp || ""} placeholder="Country code and phone number"
            onChange={(event) => update("social_whatsapp", event.target.value)} className="mt-1.5" data-testid="social_whatsapp" />
          {whatsappHref && <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-brand-700 hover:underline">
            Preview WhatsApp link <ExternalLink className="h-3 w-3" />
          </a>}
        </div>
      </div>
    </Card>
  );
}

function responseItems(data, candidates) {
  if (Array.isArray(data)) return data;
  for (const key of candidates) if (Array.isArray(data?.[key])) return data[key];
  return [];
}

function recordId(record) {
  return record?.id ?? record?.referral_id ?? record?.ticket_id ?? record?._id;
}

function ApiError({ children }) {
  return <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{children}</div>;
}

function ReferralRow({ item, onStatus, onDelete, busy }) {
  const id = recordId(item);
  const status = ["new", "contacted", "converted", "closed"].includes(item.status) ? item.status : "new";
  return (
    <div className="grid gap-3 border-t py-4 sm:grid-cols-[1fr_150px_auto] sm:items-center">
      <div className="min-w-0">
        <div className="font-medium">{item.name || "Unnamed referral"}</div>
        <div className="text-sm text-muted-foreground break-words">{[item.email, item.phone].filter(Boolean).join(" · ") || "No contact details"}</div>
        {item.notes && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{item.notes}</p>}
      </div>
      <Select value={status} onValueChange={(value) => onStatus(id, value)} disabled={busy || id == null}>
        <SelectTrigger aria-label={`Status for ${item.name || "referral"}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="new">New</SelectItem><SelectItem value="contacted">Contacted</SelectItem>
          <SelectItem value="converted">Converted</SelectItem><SelectItem value="closed">Closed</SelectItem>
        </SelectContent>
      </Select>
      <Button type="button" variant="outline" size="sm" onClick={() => onDelete(id)} disabled={busy || id == null} aria-label={`Delete referral ${item.name || ""}`}>
        <Trash2 className="h-4 w-4 sm:mr-1" /><span className="sm:inline hidden">Delete</span>
      </Button>
    </div>
  );
}

export function ReferralSettings({ http: api = http } = {}) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/settings/referrals");
      setItems(responseItems(data, ["referrals", "items", "results"]));
    } catch (e) {
      setError(requestErrorMessage(e, "load referral leads"));
    } finally {
      setLoading(false);
    }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const create = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || (!form.email.trim() && !form.phone.trim())) {
      setError("Enter a name and at least an email address or phone number.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.post("/settings/referrals", {
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
        notes: form.notes.trim(), status: "new",
      });
      setForm({ name: "", email: "", phone: "", notes: "" });
      await load();
    } catch (e) {
      setError(requestErrorMessage(e, "add this referral lead"));
    } finally {
      setSaving(false);
    }
  };
  const changeStatus = async (id, status) => {
    if (id == null) return;
    setBusyId(id);
    setError("");
    try {
      await api.patch(`/settings/referrals/${encodeURIComponent(id)}`, { status });
      await load();
    } catch (e) {
      setError(requestErrorMessage(e, "update this referral"));
    } finally {
      setBusyId(null);
    }
  };
  const remove = async (id) => {
    if (id == null || !window.confirm("Delete this referral lead?")) return;
    setBusyId(id);
    setError("");
    try {
      await api.delete(`/settings/referrals/${encodeURIComponent(id)}`);
      await load();
    } catch (e) {
      setError(requestErrorMessage(e, "delete this referral"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="referral-settings">
      <Card className="p-5 space-y-4">
        <div><h3 className="font-semibold">Company referral leads</h3><p className="text-sm text-muted-foreground mt-1">Track people referred to your company. This is an internal lead list.</p></div>
        <form onSubmit={create} className="grid gap-3 sm:grid-cols-2">
          <div><Label htmlFor="referral-name">Name</Label><Input id="referral-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" required /></div>
          <div><Label htmlFor="referral-email">Email</Label><Input id="referral-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
          <div><Label htmlFor="referral-phone">Phone</Label><Input id="referral-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
          <div><Label htmlFor="referral-notes">Notes</Label><Textarea id="referral-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Button type="submit" disabled={saving}><Plus className="h-4 w-4 mr-1" />{saving ? "Adding…" : "Add referral lead"}</Button></div>
        </form>
      </Card>
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div><h3 className="font-semibold">Referral leads</h3><p className="text-sm text-muted-foreground">Update lead status or remove a lead.</p></div>
          <Button type="button" variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
        </div>
        {error && <ApiError>{error}</ApiError>}
        {loading ? <p className="py-5 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading referral leads…</p>
          : items.length === 0 ? <p className="py-5 text-sm text-muted-foreground">No referral leads yet.</p>
            : items.map((item, index) => <ReferralRow key={recordId(item) ?? index} item={item} onStatus={changeStatus} onDelete={remove} busy={busyId === recordId(item)} />)}
      </Card>
    </div>
  );
}

function SupportRow({ item, onStatus, busy, canManage }) {
  const id = recordId(item);
  const status = ["open", "in_progress", "resolved"].includes(item.status) ? item.status : "open";
  return (
    <div className="border-t py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="font-medium">{item.subject || "Support request"}</div>
          <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap break-words">{item.message || ""}</p>
          {(item.requester_name || item.created_at) && <p className="text-xs text-muted-foreground mt-2">{[item.requester_name, item.created_at ? new Date(item.created_at).toLocaleString() : ""].filter(Boolean).join(" · ")}</p>}
        </div>
        {canManage ? <Select value={status} onValueChange={(value) => onStatus(id, value)} disabled={busy || id == null}>
          <SelectTrigger className="sm:w-44" aria-label={`Status for ${item.subject || "support request"}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="open">Open</SelectItem><SelectItem value="in_progress">In progress</SelectItem><SelectItem value="resolved">Resolved</SelectItem></SelectContent>
        </Select> : <span className="text-xs rounded-full bg-slate-100 px-2.5 py-1 self-start">{status.replace("_", " ")}</span>}
      </div>
    </div>
  );
}

export function SupportSettings({ http: api = http } = {}) {
  const { can } = useAuth();
  const canManage = can("manage_settings");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ subject: "", message: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/settings/support-tickets");
      setItems(responseItems(data, ["tickets", "support_tickets", "items", "results"]));
    } catch (e) {
      setError(requestErrorMessage(e, "load company support requests"));
    } finally {
      setLoading(false);
    }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const create = async (event) => {
    event.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      setError("Enter both a subject and a message.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.post("/settings/support-tickets", { subject: form.subject.trim(), message: form.message.trim(), status: "open" });
      setForm({ subject: "", message: "" });
      await load();
    } catch (e) {
      setError(requestErrorMessage(e, "submit this support request"));
    } finally {
      setSaving(false);
    }
  };
  const changeStatus = async (id, status) => {
    if (id == null) return;
    setBusyId(id);
    setError("");
    try {
      await api.patch(`/settings/support-tickets/${encodeURIComponent(id)}`, { status });
      await load();
    } catch (e) {
      setError(requestErrorMessage(e, "update this support request"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="support-settings">
      <Card className="p-5 space-y-4">
        <div>
          <h3 className="font-semibold">Company-internal support</h3>
          <p className="text-sm text-muted-foreground mt-1">File a request for your own company team. This is not a vendor helpdesk or external support service.</p>
        </div>
        <form onSubmit={create} className="grid gap-3">
          <div><Label htmlFor="support-subject">Subject</Label><Input id="support-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" required /></div>
          <div><Label htmlFor="support-message">Message</Label><Textarea id="support-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 min-h-28" required /></div>
          <div><Button type="submit" disabled={saving}><Plus className="h-4 w-4 mr-1" />{saving ? "Submitting…" : "Submit company request"}</Button></div>
        </form>
      </Card>
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div><h3 className="font-semibold">Company support requests</h3><p className="text-sm text-muted-foreground">{canManage ? "Manage the status of company requests." : "Requests are visible according to your company access permissions."}</p></div>
          <Button type="button" variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
        </div>
        {error && <ApiError>{error}</ApiError>}
        {loading ? <p className="py-5 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading company requests…</p>
          : items.length === 0 ? <p className="py-5 text-sm text-muted-foreground">No company support requests yet.</p>
            : items.map((item, index) => <SupportRow key={recordId(item) ?? index} item={item} canManage={canManage} onStatus={changeStatus} busy={busyId === recordId(item)} />)}
      </Card>
      <p className="text-xs text-muted-foreground">Company access and administrative permissions are enforced by the server.</p>
    </div>
  );
}